import React, { useState } from 'react';
import {
  Box,
  Grid,
  TextField,
  Typography,
  Button,
  Skeleton,
  Chip,
  IconButton,
  InputAdornment,
  Paper
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import PhotoCameraIcon from '@mui/icons-material/PhotoCamera';
import { FORM_CONFIG } from './BusinessFormConstants';


const PhotosDetailsForm = ({ formData, onChange, isLoading }) => {
  const [inputValue, setInputValue] = useState('');
  const [error, setError] = useState('');
  
  const MAX_TAGS = FORM_CONFIG.maxKeywords;
  const MAX_TAG_LENGTH = FORM_CONFIG.maxKeywordLength;

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ',') {
      e.preventDefault();
      addTag();
    }
  };

  const addTag = () => {
    const tag = inputValue.trim().toLowerCase();
    if (!tag) return;

    if (tag.length > MAX_TAG_LENGTH) {
      setError(`Tags must be ${MAX_TAG_LENGTH} characters or less`);
      return;
    }

    const currentTags = formData.keywords || [];
    
    if (currentTags.length >= MAX_TAGS) {
      setError(`Maximum ${MAX_TAGS} tags allowed`);
      return;
    }

    if (currentTags.includes(tag)) {
      setError('This tag already exists');
      return;
    }

    const newTags = [...currentTags, tag];
    onChange({
      target: {
        name: 'keywords',
        value: newTags // Now storing as array directly
      }
    });
    setInputValue('');
    setError('');
  };

  const removeTag = (tagToRemove) => {
    const currentTags = formData.keywords || [];
    const newTags = currentTags.filter(tag => tag !== tagToRemove);
    onChange({
      target: {
        name: 'keywords',
        value: newTags // Maintaining array format
      }
    });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0]; // For now, let's handle one file
    if (file) {
      onChange({
        target: {
          name: 'photo',
          value: file
        }
      });
    }
  };

  if (isLoading) {
    return (
      <Box>
        <Skeleton variant="text" width="40%" height={32} sx={{ mb: 2 }} />
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <Skeleton variant="rectangular" height={100} />
            <Skeleton variant="text" width="30%" sx={{ mt: 1 }} />
          </Grid>
          <Grid item xs={12}>
            <Skeleton variant="text" width="30%" />
            <Skeleton variant="rectangular" height={120} />
          </Grid>
          <Grid item xs={12}>
            <Skeleton variant="text" width="30%" />
            <Skeleton variant="rectangular" height={56} />
          </Grid>
        </Grid>
      </Box>
    );
  }

  const currentTags = formData.keywords || [];

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Photos & Details
      </Typography>
      
      <Grid container spacing={3}>
        {/* Photo Upload Section */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            Business Photo (Optional)
          </Typography>
          <Paper
            variant="outlined"
            sx={{
              p: 3,
              textAlign: 'center',
              bgcolor: formData.photo ? 'success.light' : 'grey.50',
              borderColor: formData.photo ? 'success.main' : 'grey.300',
              borderStyle: formData.photo ? 'solid' : 'dashed',
              borderRadius: 2,
              transition: 'all 0.2s ease-in-out',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'primary.light'
              }
            }}
          >
            <Button
              variant={formData.photo ? "contained" : "outlined"}
              component="label"
              startIcon={<PhotoCameraIcon />}
              size="large"
              sx={{ 
                height: 56,
                minWidth: 200,
                color: formData.photo ? 'white' : 'primary.main'
              }}
            >
              {formData.photo ? 'Change Photo' : 'Upload Photo'}
              <input
                type="file"
                hidden
                accept={FORM_CONFIG.acceptedImageTypes}
                onChange={handleFileChange}
              />
            </Button>
            
            {formData.photo && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="success.dark" sx={{ fontWeight: 500 }}>
                  ✓ Selected: {formData.photo.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  Size: {(formData.photo.size / 1024 / 1024).toFixed(2)} MB
                </Typography>
              </Box>
            )}
            
            {!formData.photo && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Add a photo to help customers recognize your business
              </Typography>
            )}
          </Paper>
        </Grid>

        {/* Special Features Section */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            Special Features (Optional)
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Special Features"
            name="specialFeatures"
            value={formData.specialFeatures || ''}
            onChange={onChange}
            placeholder="Describe any unique features, specialties, or what makes your business stand out..."
            helperText="Optional - Highlight what makes your business unique (e.g., 'Free Wi-Fi', '24/7 service', 'Eco-friendly practices')"
            sx={{
              '& .MuiOutlinedInput-root': {
                bgcolor: 'background.paper'
              }
            }}
          />
        </Grid>

        {/* Keywords/Tags Section */}
        <Grid item xs={12}>
          <Typography variant="subtitle1" gutterBottom>
            Keywords/Tags (Optional)
          </Typography>
          <Box sx={{ mb: 2 }}>
            <TextField
              fullWidth
              label="Add Keywords"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Type keywords and press Enter to add tags"
              error={!!error}
              helperText={error || `${currentTags.length}/${MAX_TAGS} tags used. Press Enter or comma to add a tag`}
              sx={{
                '& .MuiOutlinedInput-root': {
                  bgcolor: 'background.paper'
                }
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton 
                      onClick={addTag}
                      disabled={!inputValue.trim() || currentTags.length >= MAX_TAGS}
                      color="primary"
                    >
                      <AddIcon />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
          </Box>

          {/* Tags Display */}
          {currentTags.length > 0 ? (
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2, 
                bgcolor: 'grey.50',
                borderRadius: 2
              }}
            >
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                Your keywords ({currentTags.length}):
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                {currentTags.map((tag, index) => (
                  <Chip
                    key={index}
                    label={tag}
                    onDelete={() => removeTag(tag)}
                    color="primary"
                    variant="outlined"
                    size="small"
                    sx={{
                      '& .MuiChip-deleteIcon': {
                        '&:hover': {
                          color: 'error.main'
                        }
                      }
                    }}
                  />
                ))}
              </Box>
            </Paper>
          ) : (
            <Paper 
              variant="outlined" 
              sx={{ 
                p: 2, 
                bgcolor: 'info.light', 
                borderColor: 'info.main',
                borderStyle: 'dashed',
                borderRadius: 2
              }}
            >
              <Typography variant="body2" color="info.dark" sx={{ textAlign: 'center' }}>
                💡 Add keywords to help customers find your business (e.g., "fast", "affordable", "professional", "local")
              </Typography>
            </Paper>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

export default PhotosDetailsForm;